import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Layout } from "../components/layout";
import { SEO } from "../components/SEO";
import { toast } from "sonner";
import { Phone, CheckCircle } from "lucide-react";
import { useCMSPage } from "../hooks/useCMSPage";
import { sanitizeHTML } from "../utils/sanitize";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_IMAGE = "https://images.unsplash.com/photo-1683770997177-0603bd44d070?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHx0ZWFtJTIwb2ZmaWNlJTIwZGVza3xlbnwwfHx8fDE3NzU1MDc2Njl8MA&ixlib=rb-4.1.0&q=85&w=800";

const DEFAULTS = {
  hero_label: "Contact Us",
  hero_title: "Let's Start a",
  hero_title_accent: "Conversation",
  hero_subtitle: "Have a question or ready to start your project? We'd love to hear from you.",
  info_title: "Get in Touch",
  info_text: "We're here to help you create something amazing. Reach out by phone or fill out the form, and we'll get back to you as soon as possible.",
  phone_number: "602-835-7541",
};

export const ContactPage = () => {
  const [navItems, setNavItems] = useState([]);
  const { fields: f, content } = useCMSPage("contact", DEFAULTS);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/navigation`).then((res) => setNavItems(res.data)).catch(console.error);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong. Please try again.");
    }

    setIsSubmitting(false);
  };

  return (
    <Layout navItems={navItems}>
      <SEO
        title="Contact Us"
        description="Get in touch with It's IT for your web development needs. Call 602-835-7541 or send us a message. We're here to help small businesses and non-profits."
        keywords="contact It's IT, web development inquiry, Arizona web design, phone 602-835-7541"
      />
      {/* Hero */}
      <section data-testid="contact-hero" className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-text">{f.hero_label}</span>
            <h1 className="heading-1 mt-2 mb-6">
              {f.hero_title}
              <span className="text-its-gold"> {f.hero_title_accent}</span>
            </h1>
            <p className="body-text">
              {f.hero_subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image & Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <img
                src={CONTACT_IMAGE}
                alt="Working at desk"
                className="rounded-xl shadow-2xl w-full mb-8"
                loading="lazy"
              />

              <div className="glass-card">
                <h3 className="text-xl font-semibold text-white mb-4">{f.info_title}</h3>
                <p className="text-its-offwhite/80 mb-6">
                  {f.info_text}
                </p>
                <div className="flex items-center gap-4 p-4 rounded-lg bg-its-teal/50 border border-its-teal-light/20">
                  <div className="p-3 rounded-full bg-its-gold text-its-teal">
                    <Phone size={24} />
                  </div>
                  <div>
                    <div className="text-xs text-its-teal-light uppercase tracking-wider mb-1">
                      Call Us
                    </div>
                    <a
                      href={`tel:${f.phone_number}`}
                      data-testid="contact-phone-link"
                      className="text-xl font-semibold text-white hover:text-its-gold transition-colors"
                    >
                      {f.phone_number}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {submitted ? (
                <div className="glass-card text-center py-12">
                  <CheckCircle size={64} className="text-its-gold mx-auto mb-4" />
                  <h3 className="heading-3 mb-4">Message Sent!</h3>
                  <p className="body-text">
                    Thank you for reaching out. We'll get back to you shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="glass-card space-y-6" data-testid="contact-form">
                  <h3 className="text-xl font-semibold text-white mb-2">Send a Message</h3>

                  <div>
                    <label htmlFor="contact-name" className="form-label">Name *</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="contact-input-name"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-phone" className="form-label">Phone *</label>
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Your phone number"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="form-label">Message *</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input resize-none"
                      placeholder="How can we help you?"
                      data-testid="contact-textarea-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    data-testid="contact-submit-button"
                    className="btn-primary w-full disabled:opacity-50"
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Additional CMS Content */}
      {content && (
        <section className="section-padding">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto prose-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}
            />
          </div>
        </section>
      )}
    </Layout>
  );
};

export default ContactPage;
